import React from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";
import { withRouter } from "react-router-dom";
import Tooltip from "@ats/src/components/shared/Tooltip";
import Icon from "@ats/src/components/shared/Icon";
import { PlanFeatures } from "@shared/types/organization";

interface PlanBadgeProps {
  planKey: string;
  planDisplayName: string;
  size?: "small" | "medium" | "large";
  locked?: boolean;
  className?: string;
  includeUrl?: boolean;
  feature?: PlanFeatures;
  history?: any;
  location?: any;
  onCancel?: () => void;
  includeTag?: boolean;
}

/**
 * Reusable plan badge component with signature gradient styling
 * @example
 * ```tsx
 * <PlanBadge planKey="plan_ats_tier_growth" planDisplayName="Growth" size="medium" />
 * <PlanBadge planKey="plan_ats_tier_growth" planDisplayName="Growth" size="medium" locked={true} includeUrl={true} feature={PlanFeatures.CUSTOM_DOMAIN} />
 * ```
 */
function PlanBadge({
  planKey,
  planDisplayName,
  size = "medium",
  locked = false,
  includeTag = true,
  className,
  includeUrl = false,
  feature,
  history,
  location,
  onCancel,
}: PlanBadgeProps) {
  // Don't render anything if the feature is not locked
  if (!locked) {
    return null;
  }

  const tooltipText = locked && planDisplayName === "Scale"
    ? `Upgrade to the ${planDisplayName} plan to access this feature`
    : locked && planDisplayName === "Free" ?
    "Email us at contact@polymer.co to activate our new freemium plan."
    : locked && (planDisplayName !== "Scale" && planDisplayName !== "Free") ? 
        "Upgrade to a paid plan to access this feature."
        : "You currently have access to this feature.";

  const handleClick = () => {
    if (includeUrl && history) {
      history.push({
        pathname: "/hire/settings/billing",
        state: {
          feature,
          returnPath: location?.pathname,
          orginalPathname: location?.pathname,
        },
      });
      onCancel?.();
    }
  };

  const badgeText = locked && planDisplayName === "Scale" && size === "small"
    ? planDisplayName
    : locked && planDisplayName === "Scale"
      ? `${planDisplayName} plan`
      : locked && planDisplayName !== "Scale"
        ? "Upgrade" : "View plans";

  const badge = (
    <Styled.Badge size={size} className={className} clickable={includeUrl}>
      {badgeText}
    </Styled.Badge>
  );

  const content = (
    <Styled.Container size={size} onClick={handleClick} clickable={includeUrl}>
      {locked && <Icon name="lock" />}
      {includeTag && badge}
    </Styled.Container>
  );

  // Don't render anything if neither lock nor badge should show
  if (!locked && !includeTag) {
    return null;
  }

  return <Tooltip label={tooltipText}>{content}</Tooltip>;
}

export default withRouter(PlanBadge);

/* Styled Components
======================================================= */
let Styled: any;
Styled = {};

Styled.Container = styled.div<{ size: string; clickable?: boolean }>((props) => {
  const t: any = props.theme;
  const { size, clickable } = props;

  const iconSizes = {
    small: css`
      svg {
        ${[t.mr(1), t.text.md]}
        width: 14px;
        height: 14px;
      }
    `,
    medium: css`
      svg {
        ${[t.w(4), t.h(4), t.mr(1)]}
      }
    `,
    large: css`
      svg {
        ${[t.w(5), t.h(5), t.mr(2)]}
      }
    `,
  };

  const containerMargins = {
    small: css`
      ${[t.ml(1)]}
    `,
    medium: css`
      ${[t.ml(1)]}
    `,
    large: css`
      ${[t.ml(2)]}
    `,
  };

  return css`
    label: PlanBadge_Container;
    display: flex;
    ${containerMargins[size as keyof typeof containerMargins]}
    ${iconSizes[size as keyof typeof iconSizes]}
    ${clickable && "cursor: pointer;"}

    svg {
      stroke: ${t.dark ? t.color.gray[300] : t.color.black};
    }
  `;
});

Styled.Badge = styled.div<{ size: string; clickable?: boolean }>((props) => {
  const t: any = props.theme;
  const { size, clickable } = props;

  const sizeStyles = {
    small: css`
      ${[t.h(4), t.px(2), t.rounded.xs]}
      font-size: 0.625rem;
      line-height: 1.3;
    `,
    medium: css`
      ${[t.h(5), t.px(2), t.rounded.sm]}
      font-size: 0.75rem;
      line-height: 1.4;
    `,
    large: css`
      ${[t.h(6), t.px(3), t.rounded.md]}
      font-size: 0.875rem;
      line-height: 1.5;
    `,
  };

  return css`
    label: PlanBadge;
    ${sizeStyles[size as keyof typeof sizeStyles]}
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-weight: 400;
    color: ${t.color.black};
    background: linear-gradient(84.47deg, #fbd7ff 0%, #ffe4cc 100%, #ffdec1 100%);
    flex-shrink: 0;
    white-space: nowrap;
    transition: opacity 0.2s ease;
    ${clickable && "cursor: pointer;"}

    ${
      clickable &&
      `
      &:hover {
        opacity: 0.9;
      }
    `
    }
  `;
});
